import {
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState } from "react";
import { textFieldStyles } from "./theme";
import { useCreateUser } from "@src/entities/login";

import styles from "./styles.module.scss";
import { reachGoal } from "@src/shared/lib/analytics/yandex/reachGoal";
import { YM_GOALS } from "@src/shared/lib/analytics/yandex/constants";

interface RegistrationStep4Props {
  password: string;
  confirmPassword: string;
}

interface Props {
  setStep: (step: number) => void;
  setIsAllFieldsReady: (isAllFieldsReady: boolean) => void;
  setRegistrationData: (data: any) => void;
}

export const RegistrationStep4 = ({
  setStep,
  setIsAllFieldsReady,
  setRegistrationData,
}: Props) => {
  const [agreement, setAgreement] = useState({
    userAgreement: true,
    privacyPolicy: true,
    processingPersonalData: true,
    instructionProcessingPersonalData: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { isPending } = useCreateUser();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RegistrationStep4Props>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "onTouched",
  });

  const onSubmit = (data: RegistrationStep4Props) => {
    reachGoal(YM_GOALS.REGISTRATION_STEP(4));

    setIsAllFieldsReady(true);
    setRegistrationData((prev: any) => ({
      ...prev,
      password: data.password.trim(),
    }));
  };

  const isBtnDisabled = Object.values(agreement).includes(false) || isPending;

  return (
    <>
      <div className={styles.formTitleContainer}>
        <Typography className={styles.formTitle}>Регистрация</Typography>
        <Typography className={styles.formTitle2}>Шаг 4</Typography>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Controller
          name="password"
          control={control}
          rules={{
            required: "Поле обязательно для заполнения",
            minLength: {
              value: 8,
              message: "Пароль должен быть не менее 8 символов",
            },
            validate: {
              hasLetter: (value) =>
                /[A-Za-z]/.test(value) || "Добавьте хотя бы одну букву",
              hasNumber: (value) =>
                /\d/.test(value) || "Добавьте хотя бы одну цифру",
              hasSpecialChar: (value) =>
                /[@$!%*#?&]/.test(value) ||
                "Добавьте хотя бы один специальный символ (@$!%*#?&)",
              matchesPassword: (value) =>
                value === control._formValues.password || "Пароли не совпадают",
            },
          }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<RegistrationStep4Props, "password">;
          }) => (
            <FormControl
              className={styles.emailInput}
              sx={textFieldStyles}
              variant="outlined"
              fullWidth
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                sx={{ color: "#ffffff83" }}
              >
                Password
              </InputLabel>
              <OutlinedInput
                {...field}
                sx={{
                  borderRadius: 24,
                  color: "#fff",
                }}
                error={!!errors.password}
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      sx={{ color: "#fff", mr: 1 }}
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
              />
              <FormHelperText error={!!errors.password}>
                {errors.password?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: "Поле обязательно для заполнения",
            minLength: {
              value: 8,
              message: "Пароль должен быть не менее 8 символов",
            },
            validate: {
              hasLetter: (value) =>
                /[A-Za-z]/.test(value) || "Добавьте хотя бы одну букву",
              hasNumber: (value) =>
                /\d/.test(value) || "Добавьте хотя бы одну цифру",
              hasSpecialChar: (value) =>
                /[@$!%*#?&]/.test(value) ||
                "Добавьте хотя бы один специальный символ (@$!%*#?&)",
              matchesPassword: (value) =>
                value === control._formValues.password || "Пароли не совпадают",
            },
          }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<
              RegistrationStep4Props,
              "confirmPassword"
            >;
          }) => (
            <FormControl
              className={styles.emailInput}
              sx={textFieldStyles}
              variant="outlined"
              fullWidth
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                sx={{ color: "#ffffff83" }}
              >
                Password
              </InputLabel>
              <OutlinedInput
                {...field}
                sx={{
                  borderRadius: 24,
                  color: "#fff",
                }}
                error={!!errors.confirmPassword}
                type={showConfirmPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      sx={{ color: "#fff", mr: 1 }}
                      onClick={() => setShowConfirmPassword((show) => !show)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
              />
              <FormHelperText error={!!errors.confirmPassword}>
                {errors.confirmPassword?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
        <div className={styles.agreementLinkWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreement.userAgreement}
                onChange={(e) =>
                  setAgreement((state) => ({
                    ...state,
                    userAgreement: e.target.checked,
                  }))
                }
                sx={{
                  color: "#00FFB0",
                  "&.Mui-checked": {
                    color: "#00FFB0",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 28,
                  },
                }}
              />
            }
            label={""}
            sx={{
              alignItems: "center",
              ".MuiFormControlLabel-label": {
                color: "#fff",
                fontSize: "20px",
                fontWeight: 500,
              },
              marginRight: 0,
            }}
          />
          <a
            href="/docs/user-agreement.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.agreementLink}
          >
            Пользовательское соглашение
          </a>
        </div>
        <div className={styles.agreementLinkWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreement.privacyPolicy}
                onChange={(e) =>
                  setAgreement((state) => ({
                    ...state,
                    privacyPolicy: e.target.checked,
                  }))
                }
                sx={{
                  color: "#00FFB0",
                  "&.Mui-checked": {
                    color: "#00FFB0",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 28,
                  },
                }}
              />
            }
            label={""}
            sx={{
              alignItems: "center",
              ".MuiFormControlLabel-label": {
                color: "#fff",
                fontSize: "20px",
                fontWeight: 500,
              },
              marginRight: 0,
            }}
          />
          <a
            href="/docs/privacy_policy.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.agreementLink}
          >
            Политика конфиденциальности
          </a>
        </div>
        <div className={styles.agreementLinkWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreement.processingPersonalData}
                onChange={(e) =>
                  setAgreement((state) => ({
                    ...state,
                    processingPersonalData: e.target.checked,
                  }))
                }
                sx={{
                  color: "#00FFB0",
                  "&.Mui-checked": {
                    color: "#00FFB0",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 28,
                  },
                }}
              />
            }
            label={""}
            sx={{
              alignItems: "center",
              ".MuiFormControlLabel-label": {
                color: "#fff",
                fontSize: "20px",
                fontWeight: 500,
              },
              marginRight: 0,
            }}
          />
          <a
            href="/docs/consent-to-PD-processing.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.agreementLink}
          >
            Согласие на обработку персональных данных
          </a>
        </div>
        <div className={styles.agreementLinkWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreement.instructionProcessingPersonalData}
                onChange={(e) =>
                  setAgreement((state) => ({
                    ...state,
                    instructionProcessingPersonalData: e.target.checked,
                  }))
                }
                sx={{
                  color: "#00FFB0",
                  "&.Mui-checked": {
                    color: "#00FFB0",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 28,
                  },
                }}
              />
            }
            label={""}
            sx={{
              alignItems: "center",
              ".MuiFormControlLabel-label": {
                color: "#fff",
                fontSize: "20px",
                fontWeight: 500,
              },
              marginRight: 0,
            }}
          />
          <a
            href="/docs/order-for-PD-processing.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.agreementLink}
          >
            Поручение на обработку персональных данных
          </a>
        </div>
        <Button
          disabled={isBtnDisabled || !isValid}
          type="submit"
          variant="contained"
          className={styles.button}
          sx={{
            textTransform: "none",
            borderRadius: 24,
            mt: 6,
            color: "#222",
            fontWeight: 600,
            background: "linear-gradient(90deg, #00FFB0 0%, #00E0FF 100%)",
            "&.Mui-disabled": {
              background: "#bfc9d1",
              color: "#00000026",
            },
          }}
        >
          Зарегистрироваться
        </Button>
      </form>
      <Button
        variant="text"
        onClick={() => setStep(3)}
        sx={{
          color: "#bfc9d1",
          fontSize: "17px",
          mt: 2,
          ml: 0,
          textAlign: "left",
          p: 0,
          minWidth: 0,
          alignSelf: "flex-start",
          textTransform: "none",
          "&:hover": {
            color: "#fff",
            background: "none",
          },
        }}
      >
        Вернуться
      </Button>
    </>
  );
};
